import { useState } from "react";
import { X } from "lucide-react";
import { ExtendedProject } from "@/types/project";
import { Stage } from "@/types/pipeline";

// ============================================================================
// Types
// ============================================================================

interface ProjectPipelineStagePanelProps {
  isOpen: boolean;
  onClose: () => void;
  project: ExtendedProject | null;
  stages: Stage[];
  onStageSelect: (stageId: number) => Promise<void>;
}

// ============================================================================
// Component
// ============================================================================

export function ProjectPipelineStagePanel({
  isOpen,
  onClose,
  project,
  stages,
  onStageSelect,
}: ProjectPipelineStagePanelProps) {
  const [stageSearch, setStageSearch] = useState("");

  const handleClose = () => {
    setStageSearch("");
    onClose();
  };

  const handleStageSelect = async (stageId: number) => {
    await onStageSelect(stageId);
    // Note: Don't close here - let parent handle based on success
  };

  const filteredStages = stages.filter((stage) =>
    stage.name.toLowerCase().includes(stageSearch.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div className="bg-background rounded-lg shadow-xl w-full max-w-[400px] max-h-[80vh] flex flex-col p-6 relative mx-auto">
        <button
          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
          onClick={handleClose}
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        <h3 className="text-lg font-semibold mb-4 flex-shrink-0">
          Select Stage
        </h3>
        <div className="flex flex-col min-h-0 flex-1">
          <input
            type="text"
            placeholder="Search stages..."
            className="w-full border rounded px-3 py-2 mb-2 flex-shrink-0"
            value={stageSearch}
            onChange={(e) => setStageSearch(e.target.value)}
            autoFocus
          />
          <div className="flex-1 overflow-y-auto min-h-0 max-h-[50vh]">
            {filteredStages.map((stage) => (
              <div
                key={stage.id}
                className={`flex items-center p-2 rounded cursor-pointer hover:bg-muted ${
                  stage.id === project?.current_stage_id ? "bg-muted" : ""
                }`}
                onClick={() => handleStageSelect(stage.id)}
              >
                <span>{stage.name}</span>
                {stage.id === project?.current_stage_id && (
                  <span className="ml-2 text-xs text-primary font-medium">
                    Current
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
        <button
          className="mt-4 w-full bg-muted text-primary border border-border py-2 rounded hover:bg-muted transition font-medium flex-shrink-0"
          onClick={handleClose}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

ProjectPipelineStagePanel.displayName = "ProjectPipelineStagePanel";
